import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import dayjs from "dayjs";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import type { IReturnLineItem } from "@repo/shopify/api/fetchOrder";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import type { ICaaCustomerAddress, ICaaLineItem } from "@repo/shopify/queries/customer-account-gql";
import type { IOrdersEdgesNode } from "@repo/shopify/queries/customer-gql";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import { OrderStatusTrans, getTrackingInfoCaa } from "@repo/shopify/utils/order";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useFindOrderCaa from "@hooks/useFindOrderCaa";
import useOrderStatusCaa from "@hooks/useOrderStatusCaa";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

// classic OrderDetail은 Storefront customerAccessToken 기반이라 jp(New Customer Accounts)에서 재사용 불가.
// jp는 반품(RETURN)과 주문 취소(CANCEL) 모두 제공하지 않아 상태 버튼(OrderStatusButton) 없이 상태 텍스트만 노출한다.
// CAA LineItem은 nested variant가 없어 OrderLineItem(classic 전용 컴포넌트) 형태로 매핑해 재사용한다.
// price는 discountedTotalPrice 한 곳에만 실려도 useOrderLineInfo가 정상 계산하므로 variant는 표시용 title/image만 채우면 된다.
const toOrderLineItemNode = (node: ICaaLineItem, quantity: number, returnReason?: string | null): IReturnLineItem =>
  ({
    id: node.id,
    title: node.title,
    quantity,
    originalQuantity: node.quantity,
    discountedTotalPrice: { amount: node.currentTotalPrice?.amount || node.totalPrice?.amount || "0" },
    discountAllocations: [],
    returnReason: returnReason || "",
    customerNote: "",
    variant: {
      id: node.variantId || "",
      title: node.variantTitle || "",
      price: { amount: "0" },
      image: { url: node.image?.url || "", width: 1280, height: 1707 },
      selectedOptions: [],
      product: { tags: [], vendor: "" },
    },
  }) as unknown as IReturnLineItem;

// TODO: 실제 주소로 검증 필요
const toMailingAddress = (address?: ICaaCustomerAddress | null) =>
  address
    ? ({
        firstName: address.firstName || "",
        lastName: address.lastName || "",
        phone: address.phoneNumber || "",
        address1: address.address1 || "",
        address2: address.address2 || "",
        city: address.city || "",
        province: address.province || "",
        provinceCode: address.zoneCode || "",
        country: address.country || "",
        zip: address.zip || "",
      } as unknown as MailingAddress)
    : undefined;

export default function OrderDetailJp() {
  const navigate = useNavigate();
  const { handle } = useParams();
  const { t } = useTranslation();

  const { caaToken } = useCustomerStore();
  const { setMeta, openAccount, showFooter } = useLayoutStore();

  useEffect(() => {
    window.scrollTo(0, 0);
    showFooter(true);
    setMeta({ title: `Order Detail - ${DEFAULT_TITLE}` });
  }, []);

  useEffect(() => {
    if (!caaToken?.accessToken) {
      setTimeout(() => {
        sessionStorage.setItem("loginAfterUrl", `${URLS.ORDER}${handle}`);
        openAccount(true);
      }, 300);
    }
  }, [caaToken]);

  const { order, loading, orderNotFound } = useFindOrderCaa({ orderId: handle });

  const { orderStatus } = useOrderStatusCaa(order);

  // 배송 송장 정보
  const trackingInfo = getTrackingInfoCaa(order);

  const paymentInfo = useOrderPayMethod({ lang: "jp", order: order as unknown as IOrdersEdgesNode | null });

  useEffect(() => {
    if (!loading && orderNotFound) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [loading, orderNotFound]);

  const shippingAddress = useMemo(() => formatOrderAddress({ address: toMailingAddress(order?.shippingAddress), lang: "jp" }), [order]);
  const billingAddress = useMemo(() => formatOrderAddress({ address: toMailingAddress(order?.billingAddress), lang: "jp" }), [order]);

  const subTotalAmount = parseFloat(order?.subtotal?.amount || "0");
  const shippingAmount = parseFloat(order?.totalShipping?.amount || "0");
  const taxAmount = parseFloat(order?.totalTax?.amount || "0");
  const totalAmount = parseFloat(order?.totalPrice?.amount || "0");
  const refundAmount = parseFloat(order?.totalRefunded?.amount || "0");
  const dutyAmount = Math.max(0, totalAmount - subTotalAmount - taxAmount - shippingAmount);

  // classic useOrderInfo의 autoDiscountAmount/codeDiscountAmount 분리 로직과 동일한 기준.
  // discountApplication.__typename은 classic Storefront API와 동일해서(Automatic/Manual/DiscountCodeApplication)
  // 같은 분류 방식을 그대로 적용할 수 있다.
  const { autoDiscountAmount, autoDiscountTitle, codeDiscountAmount, codeDiscountTitle } = useMemo(() => {
    const allocations = order?.lineItems.edges.flatMap(({ node }) => node.discountAllocations) ?? [];

    const autoAllocations = allocations.filter(
      ({ discountApplication }) =>
        discountApplication.__typename === "AutomaticDiscountApplication" || discountApplication.__typename === "ManualDiscountApplication",
    );
    const codeAllocations = allocations.filter(({ discountApplication }) => discountApplication.__typename === "DiscountCodeApplication");

    const autoTitles = new Set<string>();
    autoAllocations.forEach(({ discountApplication }) => {
      if (discountApplication.__typename !== "DiscountCodeApplication") {
        autoTitles.add(discountApplication.title);
      }
    });

    let autoTitle = Array.from(autoTitles).join(", ");
    if (autoTitles.has("Employee")) autoTitle = "Employee";
    else if (autoTitles.has("Friends")) autoTitle = "Friends";

    const codeTitles = new Set<string>();
    codeAllocations.forEach(({ discountApplication }) => {
      if (discountApplication.__typename === "DiscountCodeApplication") {
        codeTitles.add(discountApplication.code);
      }
    });

    return {
      autoDiscountAmount: autoAllocations.reduce((sum, item) => sum + parseFloat(item.allocatedAmount.amount || "0"), 0),
      autoDiscountTitle: autoTitle,
      codeDiscountAmount: codeAllocations.reduce((sum, item) => sum + parseFloat(item.allocatedAmount.amount || "0"), 0),
      codeDiscountTitle: Array.from(codeTitles).join(", "),
    };
  }, [order]);

  const { completeLineItems, refundedLineItems } = useMemo(() => {
    const edges = order?.lineItems.edges ?? [];

    if (order?.financialStatus === "REFUNDED") {
      return { completeLineItems: [], refundedLineItems: edges };
    }

    const complete: typeof edges = [];
    const refunded: typeof edges = [];

    edges.forEach((edge) => {
      const { quantity, refundableQuantity } = edge.node;
      const refundedQty = quantity - refundableQuantity;

      if (refundableQuantity > 0) {
        complete.push({ node: { ...edge.node, quantity: refundableQuantity } });
      }
      if (refundedQty > 0) {
        refunded.push({ node: { ...edge.node, quantity: refundedQty } });
      }
    });

    return { completeLineItems: complete, refundedLineItems: refunded };
  }, [order]);

  return (
    <>
      {!order || loading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
          <section className="pc:col-span-10 pc:flex-col col-span-8 flex justify-between gap-5">
            <section className="pc:text-[1.125rem] flex flex-col gap-1 text-lg leading-none">
              <h2>{order.name || ""}</h2>
              <div className="pc:flex-row pc:gap-2.5 flex flex-col gap-2">
                <span>{dayjs(order?.processedAt).format("MMM DD, YYYY")}</span>
                <div className="flex gap-2.5">
                  <span className="text-md pc:text-lg leading-none">{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
                  {trackingInfo ? (
                    <Link as="text" className="pc:text-lg! w-fit leading-none font-normal underline" href={trackingInfo.url ?? ""}>
                      {trackingInfo.number}
                    </Link>
                  ) : null}
                </div>
              </div>
            </section>
          </section>

          <section className="pc:col-span-10 pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-10">
            <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-5 leading-none">
              {trackingInfo?.company ? (
                <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                  <span>Shipping Method</span>
                  <span className="font-normal">{trackingInfo.company}</span>
                </div>
              ) : null}

              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span>Shipping Address</span>
                <Address {...shippingAddress} />
              </div>

              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span>Billing Address</span>
                <Address {...billingAddress} />
              </div>
            </section>

            <section className="pc:col-span-5 pc:col-start-4 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10 leading-none">
              {completeLineItems.length > 0 ? (
                <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
                  <span className="pc:col-span-5 col-span-8">Line items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {completeLineItems.map(({ node }) => (
                      <OrderLineItem key={node.id} node={toOrderLineItemNode(node, node.quantity)} />
                    ))}
                  </ul>
                </div>
              ) : null}

              {refundedLineItems.length > 0 ? (
                <div className="tnt-gap-x col-span-8 flex flex-col gap-y-5">
                  <span className="pc:col-span-5 col-span-8">Refunded Items</span>
                  <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                    {refundedLineItems.map(({ node }) => (
                      <OrderLineItem key={node.id} node={toOrderLineItemNode(node, node.quantity)} />
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="pc:items-end pc:col-start-4 col-span-8 flex w-full flex-col">
                <div className="pc:w-screen-col-span-2 flex flex-col gap-1">
                  <OrderPaymentInfo
                    autoDiscountAmount={autoDiscountAmount}
                    autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                    codeDiscountAmount={codeDiscountAmount}
                    codeDiscountTitle={codeDiscountTitle}
                    dutyAmount={dutyAmount}
                    originalTotalAmount={subTotalAmount}
                    refundAmount={refundAmount}
                    shippingFee={shippingAmount}
                    taxAmount={taxAmount}
                    totalAmount={totalAmount}
                  />
                </div>
              </div>
            </section>
          </section>
        </section>
      )}
    </>
  );
}
